import { motion } from 'framer-motion';
import {
  Presentation,
  Target,
  Users,
  TrendingUp,
  FileText,
  ArrowRight,
  Phone,
  CheckCircle2,
  Lightbulb,
  BarChart3,
  Clock,
  DollarSign,
  Award,
  Building
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const packages = [
  {
    name: 'Business Proposal & Pitch Deck Consulting',
    description: 'Penyusunan dan optimasi proposal/pitch deck yang terstruktur, meyakinkan, dan investor-ready.',
    features: [
      'Storytelling Framework Development',
      'Financial Numbers & Projections',
      'Value Proposition Strengthening',
      'Investor-Ready Presentation Design'
    ],
    output: 'Comprehensive pitch deck, executive summary, financial projections, investor Q&A prep',
    icon: Presentation,
    popular: false
  },
  {
    name: 'Proposal / Pitch Deck Validation',
    description: 'Evaluasi objektif terhadap proposal atau pitch deck Anda untuk meningkatkan peluang approval.',
    features: [
      'Comprehensive Document Review',
      'Weakness & Blind Spot Identification',
      'Strategic Improvement Recommendations',
      'Funding Readiness Assessment'
    ],
    output: 'Validation report, improvement roadmap, risk mitigation strategy, approval optimization',
    icon: CheckCircle2,
    popular: true
  }
];

const proposalStructure = [
  {
    icon: FileText,
    title: 'Title Page',
    description: 'Halaman pembuka profesional & representatif'
  },
  {
    icon: Target,
    title: 'Executive Summary',
    description: 'Rangkuman peluang pasar & tujuan bisnis'
  },
  {
    icon: Users,
    title: 'Introduction',
    description: 'Latar belakang usaha & produk/jasa termasuk analisis pasar'
  },
  {
    icon: BarChart3,
    title: 'Benchmark Competitor',
    description: 'Identifikasi pesaing utama di industri'
  },
  {
    icon: Lightbulb,
    title: 'Problem Statement',
    description: 'Identifikasi tantangan utama di industri'
  },
  {
    icon: Target,
    title: 'Value Proposition Mapping',
    description: 'Value Delivery & Sasaran Segmentasi Bisnis'
  },
  {
    icon: TrendingUp,
    title: 'Business Strategy',
    description: 'Strategi bisnis & struktur tim atau traction'
  },
  {
    icon: Clock,
    title: 'Timeline',
    description: 'Perkiraan waktu implementasi proyek'
  },
  {
    icon: DollarSign,
    title: 'Financial Projection',
    description: 'Garis besar proyeksi keuangan'
  },
  {
    icon: FileText,
    title: 'Kebutuhan Modal Dasar',
    description: 'Analisis dasar biaya & modal awal'
  },
  {
    icon: TrendingUp,
    title: 'ROI & BEP',
    description: 'Estimasi pengembalian investasi & titik impas'
  },
  {
    icon: Award,
    title: 'Conclusion',
    description: 'Rangkuman keseluruhan proposal'
  }
];

const suitableFor = [
  {
    icon: Lightbulb,
    title: 'Startup & Perusahaan',
    description: 'Yang mencari pendanaan/investor'
  },
  {
    icon: TrendingUp,
    title: 'Bisnis Ekspansi',
    description: 'Yang ingin menyusun strategi ekspansi & growth'
  },
  {
    icon: Building,
    title: 'Perusahaan',
    description: 'Yang membutuhkan blueprint bisnis solid untuk pendanaan'
  }
];

export function BusinessProposalPitchDeck() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: packagesRef, isInView: packagesInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: structureRef, isInView: structureInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/business-proposal-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">04</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Business Proposal & Pitch Deck
            </h1>

            <p className="text-xl text-white/70 mb-4 leading-relaxed">
              <strong className="text-primary-400">Blueprint Bisnis Profesional</strong> untuk Menarik Investor & Pendanaan
            </p>

            <p className="text-white/60 mb-8">
              Kami membantu menyusun business proposal & pitch deck yang komprehensif, strategis, dan persuasif untuk keperluan fundraising, pitching ke investor, dan strategi bisnis.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Packages Section - NOW ABOVE Structure */}
      <section ref={packagesRef} className="py-9">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={packagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              Paket Layanan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto mb-8">
              Solusi komprehensif untuk kebutuhan proposal dan pitch deck Anda
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                animate={packagesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300 ${
                  pkg.popular ? 'border-2 border-primary-500/50 relative' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
                
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <pkg.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">{pkg.name}</h3>
                    <p className="text-white/60 text-sm">{pkg.description}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-white/70">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-white/50 mb-1">Output:</p>
                  <p className="text-sm text-white/70">{pkg.output}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Structure Section - NOW BELOW Packages */}
      <section ref={structureRef} className="pt-6 pb-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={structureInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Struktur Business Proposal & Pitch Deck
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Format bisa bersifat custom bergantung pada kondisi dan latar belakang usaha ataupun request dari klien
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proposalStructure.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={structureInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable For Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/proposal-suitable-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Cocok Untuk
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {suitableFor.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lightbulb className="w-6 h-6 text-primary-400" />
            </div>
            <p className="text-white/80 text-lg mb-4">
              Kami juga membantu <strong className="text-primary-400">ideation & strategi skema investasi</strong> agar proposal Anda lebih menarik dan sesuai dengan target investor!
            </p>
            
            <p className="text-white/60">
              Siapkan pitch deck & business proposal yang terstruktur, persuasif, dan profesional untuk mengamankan pendanaan bisnis Anda! ✨
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Presentation className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Siap Menarik Investor?
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan business proposal & pitch deck profesional yang akan membuat investor tertarik dengan bisnis Anda.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
